import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import axios from 'axios';
import { FilterContext } from '../App';
import MultiSelect from './MultiSelect';
import {
  rollup, deriveProvinces, isProvinceDistinct, scopedCities, pruneCities, sameSelection,
} from './geoFilter';

/**
 * Inline Date + Geography filter bar.
 * Self-contained: fetches its own regions from /api/filters/dimensions on mount.
 * Reads and writes via FilterContext so any page can drop it in with no props.
 *
 * Geography is two levels, both multi-select. dim_region holds the city in
 * region_name and the province it rolls up to in the nullable `state` column, so
 * the province list is derived here with the same COALESCE fallback the backend
 * uses. Selecting provinces narrows the city list; the province filter itself is
 * enforced server-side off the `province` param, not by expanding it into city ids,
 * so "every city in this province" reflects dim_region as it is now rather than as
 * it was when the page loaded.
 */

function FilterBar() {
  const { filters, setFilters } = useContext(FilterContext);
  const [regions, setRegions] = useState([]);

  // A native <input type="date"> fires onChange for EVERY partial edit: stepping through
  // month, day and year emits three complete-but-wrong dates. Each one used to go straight
  // into the shared filter context, and every page refetches on a filter change — so
  // picking one date fired several full page loads. /api/field-intelligence alone runs
  // ~11 queries, and with only 4 gunicorn workers that storm could saturate the backend
  // and make every widget render zeros. Dates are now held locally and committed once the
  // user stops typing.
  const [draftDates, setDraftDates] = useState({
    date_from: filters.date_from || '',
    date_to: filters.date_to || '',
  });
  const dirtyRef = useRef(false);

  // Same problem, same shape of fix: ticking six cities must be one commit, not six.
  // Held as a draft and written to the context when the dropdown closes. The ref
  // mirrors the draft so the commit reads the current value rather than whatever the
  // closure captured — removing a chip changes the draft and closes in one go.
  const [geo, setGeo] = useState({ province: [], region_id: [] });
  const geoRef = useRef(geo);
  const setGeoDraft = (next) => { geoRef.current = next; setGeo(next); };

  useEffect(() => {
    axios.get('/api/filters/dimensions')
      .then(res => setRegions(res.data.regions || []))
      .catch(() => {});
  }, []);

  // Follow the context when it changes from elsewhere (e.g. the Clear button), without
  // marking the draft dirty — that would commit straight back and loop.
  useEffect(() => {
    setDraftDates({ date_from: filters.date_from || '', date_to: filters.date_to || '' });
  }, [filters.date_from, filters.date_to]);

  useEffect(() => {
    setGeoDraft({ province: filters.province || [], region_id: filters.region_id || [] });
  }, [filters.province, filters.region_id]);

  useEffect(() => {
    if (!dirtyRef.current) return;
    const t = setTimeout(() => {
      dirtyRef.current = false;
      setFilters({
        date_from: draftDates.date_from || null,
        date_to: draftDates.date_to || null,
      });
    }, 600);
    return () => clearTimeout(t);
  }, [draftDates]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDate = (key, value) => {
    dirtyRef.current = true;
    setDraftDates(d => ({ ...d, [key]: value }));
  };

  const provinces = useMemo(() => deriveProvinces(regions), [regions]);
  const provinceDistinct = isProvinceDistinct(regions, provinces);

  const provinceOptions = useMemo(
    () => provinces.map(p => ({ value: p, label: p })),
    [provinces],
  );

  const cityOptions = useMemo(() => scopedCities(regions, geo.province).map(r => ({
    value: r.region_id,
    label: r.region_name,
    // Only worth showing once the city list is the long unnarrowed one.
    sublabel: provinceDistinct && !geo.province.length ? rollup(r) : undefined,
  })), [regions, geo.province, provinceDistinct]);

  const onProvinceChange = (next) => setGeoDraft({
    province: next,
    region_id: pruneCities(regions, next, geoRef.current.region_id),
  });

  const onCityChange = (next) => setGeoDraft({ ...geoRef.current, region_id: next });

  const commitGeo = () => {
    const { province, region_id } = geoRef.current;
    const nextProvince = province.length ? province : null;
    const nextRegion = region_id.length ? region_id : null;
    // Opening and closing a dropdown without touching it must not refetch every page.
    if (sameSelection(nextProvince, filters.province || null)
      && sameSelection(nextRegion, filters.region_id || null)) return;
    setFilters({ province: nextProvince, region_id: nextRegion });
  };

  const clearAll = () => {
    dirtyRef.current = false;
    geoRef.current = { province: [], region_id: [] };
    setGeo(geoRef.current);
    setFilters({ date_from: null, date_to: null, province: null, region_id: null });
  };

  const hasActive = !!(
    filters.date_from || filters.date_to
    || (filters.province && filters.province.length)
    || (filters.region_id && filters.region_id.length)
  );

  const fieldStyle = {
    display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 180px', minWidth: '180px',
  };
  const labelStyle = {
    fontSize: '10px', fontWeight: 900, textTransform: 'uppercase',
    letterSpacing: '0.18em', color: 'var(--text-lighter)',
  };
  const inputStyle = {
    border: '1.5px solid var(--card-border)', borderRadius: '12px',
    padding: '10px 14px', fontSize: '14px', background: 'var(--card-bg)',
    color: 'var(--text-dark)', outline: 'none', width: '100%', boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  return (
    <div style={{
      background: 'var(--card-bg)',
      border: '1px solid var(--card-border)',
      borderRadius: '20px',
      padding: '20px 24px',
      marginBottom: '32px',
      display: 'flex',
      gap: '20px',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={fieldStyle}>
        <label style={labelStyle}>Date From</label>
        <input
          type="date"
          style={inputStyle}
          value={draftDates.date_from}
          onChange={e => setDate('date_from', e.target.value)}
        />
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle}>Date To</label>
        <input
          type="date"
          style={inputStyle}
          value={draftDates.date_to}
          onChange={e => setDate('date_to', e.target.value)}
        />
      </div>
      {provinceDistinct && (
        <div style={fieldStyle}>
          <label style={labelStyle}>Province / State</label>
          <MultiSelect
            options={provinceOptions}
            selected={geo.province}
            onChange={onProvinceChange}
            onClose={commitGeo}
            placeholder="All provinces"
            name="provinces"
          />
        </div>
      )}
      <div style={fieldStyle}>
        <label style={labelStyle}>City</label>
        <MultiSelect
          options={cityOptions}
          selected={geo.region_id}
          onChange={onCityChange}
          onClose={commitGeo}
          placeholder={geo.province.length ? 'All cities in selection' : 'All cities'}
          name="cities"
        />
      </div>
      {hasActive && (
        <button
          type="button"
          onClick={clearAll}
          style={{
            padding: '10px 16px',
            border: '1.5px solid var(--card-border)',
            borderRadius: '12px',
            background: 'transparent',
            color: 'var(--text-light)',
            fontSize: '13px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            alignSelf: 'flex-end',
            whiteSpace: 'nowrap',
          }}
        >
          Clear
        </button>
      )}
    </div>
  );
}

export default FilterBar;
